import { useState, useEffect, createContext, useContext } from 'react';
import * as api from './api';

// Local storage keys
const AUTH_TOKEN_KEY = 'rumahkira_auth_token';
const USER_DATA_KEY = 'rumahkira_user_data';

// Legacy free trial keys (gating now handled by useGate + SubscriptionContext)
export const FREE_USES_KEY = 'rumahkira_free_uses_count';
export const FREE_USES_RESET_TIME_KEY = 'rumahkira_free_uses_reset_time';
export const FREE_USES_MAX = 2;

// Auth context
export const AuthContext = createContext();

// Custom hook for using auth context
export const useAuth = () => useContext(AuthContext);

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem(AUTH_TOKEN_KEY);
};

// Get user data
export const getUserData = () => {
  const userData = localStorage.getItem(USER_DATA_KEY);
  return userData ? JSON.parse(userData) : null;
};

// Set user data
export const setUserData = (userData) => {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
};

// Access Pass types
const ACCESS_PASS_TYPES = ['weekly', 'premium'];

/**
 * Check if a user's subscription is active (handles Access Pass types)
 */
const isSubscriptionActive = (userData) => {
  if (!userData) return false;
  const subType = userData.subscription_type || userData.subscriptionType;
  if (subType === 'free') return false;
  // Access Pass or legacy types: trust server's subscription_active flag
  if (userData.subscription_active !== undefined) return userData.subscription_active;
  // Fallback: check expiry
  const expires = userData.subscription_expires || userData.subscriptionExpires;
  if (expires) return new Date(expires) > new Date();
  return false;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      if (isAuthenticated()) {
        // Try to get user data from API
        try {
          const response = await api.getProfile();
          if (response.success) {
            const userData = response.data;
            setUserData(userData);
            setUser(userData);
          }
        } catch (error) {
          // If API fails, try local data
          const localUser = getUserData();
          if (localUser) {
            setUser(localUser);
          } else {
            // Token invalid, clear it
            api.removeToken();
          }
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Sign-in function - now calls real API
  const signIn = async (email, password) => {
    try {
      const response = await api.loginUser(email, password);

      if (response.success) {
        const userData = response.data.user;

        // Transform API response to match our user data structure
        const transformedUser = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          subscription: userData.subscription_type === 'free' ? 'free' : 'premium',
          subscriptionType: userData.subscription_type,
          subscriptionActive: userData.subscription_active,
          subscriptionExpires: userData.subscription_expires,
          freeUsesRemaining: userData.free_uses_remaining,
          totalEstimates: userData.total_estimates,
          createdAt: userData.created_at
        };

        // Store auth data
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
        setUserData(transformedUser);

        // Update state
        setUser(transformedUser);

        return transformedUser;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    }
  };

  // Sign-up function - now calls real API
  const signUp = async (email, password, name, phone = null) => {
    try {
      const response = await api.registerUser(name, email, password, phone);

      if (response.success) {
        const userData = response.data.user;

        // Transform API response
        const transformedUser = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          subscription: 'free',
          subscriptionType: userData.subscription_type,
          freeUsesRemaining: userData.free_uses_remaining,
          createdAt: new Date().toISOString()
        };

        // Store auth data
        localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
        setUserData(transformedUser);

        // Update state
        setUser(transformedUser);

        return transformedUser;
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  // Sign out function
  const signOut = () => {
    api.logoutUser();
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    setUser(null);
  };

  // Refresh user data from API
  const refreshUser = async () => {
    if (!isAuthenticated()) return null;

    try {
      const response = await api.getProfile();
      if (response.success) {
        const userData = response.data;
        const transformedUser = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          phone: userData.phone,
          subscription: userData.subscription_type === 'free' ? 'free' : 'premium',
          subscriptionType: userData.subscription_type,
          subscriptionActive: userData.subscription_active,
          subscriptionExpires: userData.subscription_expires,
          freeUsesRemaining: userData.free_uses_remaining,
          totalEstimates: userData.total_estimates,
          createdAt: userData.created_at
        };
        setUserData(transformedUser);
        setUser(transformedUser);
        return transformedUser;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
    return null;
  };

  // Reset app data
  const resetAppData = () => {
    if (user) {
      const keysToPreserve = [AUTH_TOKEN_KEY, USER_DATA_KEY];
      Object.keys(localStorage).forEach(key => {
        if (!keysToPreserve.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    }
    return false;
  };

  // Check if feature is available
  const isFeatureAvailable = (feature, subfeature = null) => {
    if (user) {
      // Active Access Pass holders have full access
      if (ACCESS_PASS_TYPES.includes(user.subscriptionType) && user.subscriptionActive) return true;
      // Legacy premium
      if (user.subscriptionType === 'premium') return true;
      if (user.subscriptionType === 'monthly' && user.subscriptionActive) return true;
      // Free users have limited access
      if (user.freeUsesRemaining > 0) return true;
    }
    // Non-authenticated users use local free usage count
    const count = getFreeUsesCount();
    return count < FREE_USES_MAX;
  };

  // Check if user can use feature (considering subscription)
  const canUseFeature = () => {
    if (user) {
      // Active Access Pass holders have unlimited access
      if (ACCESS_PASS_TYPES.includes(user.subscriptionType) && user.subscriptionActive) return true;
      // Legacy premium
      if (user.subscriptionType === 'premium') return true;
      if (user.subscriptionType === 'monthly' && user.subscriptionActive) return true;
      // Free users with remaining uses
      if (user.freeUsesRemaining > 0) return true;
      return false;
    }
    // Non-authenticated users use local free trial
    const count = getFreeUsesCount();
    return count < FREE_USES_MAX;
  };

  const authContextValue = {
    user,
    loading,
    trialActive: false, // Legacy compatibility - always false now
    signIn,
    signUp,
    signOut,
    refreshUser,
    resetAppData,
    isFeatureAvailable,
    canUseFeature,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Check and reset free uses if 24 hours have elapsed
const checkAndResetFreeUses = () => {
  const resetTime = localStorage.getItem(FREE_USES_RESET_TIME_KEY);
  if (resetTime) {
    const elapsed = Date.now() - parseInt(resetTime, 10);
    if (elapsed >= 24 * 60 * 60 * 1000) {
      localStorage.setItem(FREE_USES_KEY, '0');
      localStorage.setItem(FREE_USES_RESET_TIME_KEY, Date.now().toString());
    }
  } else {
    // First time - set the reset timestamp
    localStorage.setItem(FREE_USES_RESET_TIME_KEY, Date.now().toString());
  }
};

// Usage-based free trial helper functions
export const getFreeUsesCount = () => {
  checkAndResetFreeUses();
  return parseInt(localStorage.getItem(FREE_USES_KEY) || '0', 10);
};

export const recordFreeUsage = () => {
  const count = getFreeUsesCount();
  const newCount = count + 1;
  localStorage.setItem(FREE_USES_KEY, newCount.toString());
  return newCount;
};

export const canUseFreeFeature = (user) => {
  if (user) return true;
  const count = getFreeUsesCount();
  return count < FREE_USES_MAX;
};

export const resetFreeUsage = () => {
  localStorage.removeItem(FREE_USES_KEY);
};

export default AuthProvider;
